import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AppShell from '../components/work/AppShell';
import WorkItemDetail from '../components/work/WorkItemDetail';
import { LoadingRows, WorkPageError } from '../components/work/page-states';
import { apiFetch, v1Path } from '../lib/api';
import { isProjectId } from '../lib/project-path';
import type { StateDto, WorkItemFull } from '../lib/work-types';
import { serviceRequestPath } from '../lib/service-requests';
import { initiativePath } from '../lib/initiatives';

export default function WorkItemPage() {
  const { projectId, workItemId } = useParams<{ projectId: string; workItemId: string }>();
  const [item, setItem] = useState<WorkItemFull | null>(null);
  const [states, setStates] = useState<StateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId || !workItemId) return;
    setLoading(true);
    setError(null);
    try {
      const [full, stateRes] = await Promise.all([
        apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${workItemId}`)),
        apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
      ]);
      setItem(full);
      setStates(stateRes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work item');
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, workItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!projectId || !workItemId || !isProjectId(projectId)) {
    return <div className="p-6 text-sm text-muted-foreground">Missing work item.</div>;
  }
  if (item?.type === 'initiative') {
    return <Navigate to={initiativePath(item.id)} replace />;
  }
  if (item?.type === 'service_request') {
    return <Navigate to={serviceRequestPath(item)} replace />;
  }

  return (
    <AppShell projectId={projectId}>
      <div className="max-w-5xl">
        <Link
          to={`/projects/${projectId}/tasks`}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Tasks
        </Link>
        {loading ? <LoadingRows /> : null}
        <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
        {item ? (
          <WorkItemDetail
            projectId={projectId}
            item={item}
            states={states}
            onUpdated={setItem}
            chrome="page"
          />
        ) : null}
      </div>
    </AppShell>
  );
}
